import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { extractVideoThumbnail } from "../lib/videoThumbnail";

export const SEASONS = ["2025/26", "2024/25", "2023/24", "2022/23", "2021/22"];

const uploadFile = async (path, fileOrBlob, filename, onProgress) => {
  const fd = new FormData();
  if (filename) fd.append("file", fileOrBlob, filename);
  else fd.append("file", fileOrBlob);
  const { data } = await api.post(path, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: onProgress,
  });
  return data;
};

export function usePostForm() {
  const nav = useNavigate();
  const [fields, setFields] = useState({
    caption: "", opponent: "", result: "",
    goals: 0, assists: 0, minutes: 90, season: SEASONS[0],
  });
  const [file, setFile] = useState(null);
  const [photos, setPhotos] = useState([]); // up to 4 File objects
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [device, setDevice] = useState({ stats: null, fileName: "", loading: false });
  const [error, setError] = useState("");

  const setField = (key) => (value) => setFields((f) => ({ ...f, [key]: value }));

  const numericStats = () => ({
    goals: Number(fields.goals) || 0,
    assists: Number(fields.assists) || 0,
    minutes: Number(fields.minutes) || 0,
  });

  const generateCaption = async () => {
    setError("");
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/caption", {
        opponent: fields.opponent, result: fields.result,
        season: fields.season, ...numericStats(),
      });
      setField("caption")(data.caption);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setAiLoading(false);
    }
  };

  const importDeviceFile = async (f) => {
    if (!f) return;
    setError("");
    setDevice((d) => ({ ...d, loading: true }));
    try {
      const data = await uploadFile("/device/parse", f);
      setDevice({ stats: data.device_stats, fileName: f.name, loading: false });
      if (data.minutes) setField("minutes")(data.minutes);
    } catch (err) {
      setError(formatApiError(err));
      setDevice((d) => ({ ...d, loading: false }));
    }
  };

  const clearDevice = () => setDevice({ stats: null, fileName: "", loading: false });

  const uploadVideoWithThumbnail = async () => {
    const data = await uploadFile("/upload/video", file, null, (evt) => {
      if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
    });
    let thumb_path = null;
    let duration = 0;
    try {
      const thumb = await extractVideoThumbnail(file);
      duration = thumb.duration;
      if (thumb.blob) {
        thumb_path = (await uploadFile("/upload/image", thumb.blob, "thumb.jpg")).path;
      }
    } catch (err) {
      // thumbnail is best-effort; never block publishing
      console.warn("Video thumbnail generation skipped:", err?.message || err);
    }
    return { video_path: data.path, thumb_path, duration };
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setUploading(true);
    try {
      const media = file
        ? await uploadVideoWithThumbnail()
        : { video_path: null, thumb_path: null, duration: 0 };
      const image_paths = [];
      for (const photo of photos) {
        image_paths.push((await uploadFile("/upload/image", photo)).path);
      }
      await api.post("/posts", {
        caption: fields.caption, opponent: fields.opponent, result: fields.result,
        season: fields.season, ...numericStats(), ...media,
        image_paths, device_stats: device.stats,
      });
      nav("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return {
    fields, setField, file, setFile, photos, setPhotos,
    uploadProgress, uploading, aiLoading, device, error,
    generateCaption, importDeviceFile, clearDevice, submit,
  };
}
